/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
  // theme.mediaQueries isn't defined anywhere else in the theme (BGE-only file, not vendor) -
  // without it this whole IIFE bails out before setting theme.mediaMatches, and every later
  // `theme.mediaMatches.md` read (checkCardPosition, the auto-open-on-desktop check) throws,
  // which stops a hotspot click from ever reaching setHotspotActiveState. Default to the
  // theme's own small/desktop breakpoint (theme.sizes.small, 750px, used sitewide) if unset.
  theme.mediaQueries = theme.mediaQueries || { md: `(min-width: ${theme.sizes.small}px)` };
  const { mediaQueries } = theme;
  if (!mediaQueries) return;

  const mqKeys = Object.keys(mediaQueries);
  const mqLists = {};
  theme.mediaMatches = {};

  const handleMqChange = () => {
    const newMatches = mqKeys.reduce((acc, media) => {
      acc[media] = !!(mqLists[media] && mqLists[media].matches);
      return acc;
    }, {});
    Object.keys(newMatches).forEach((key) => {
      theme.mediaMatches[key] = newMatches[key];
    });
    window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
  };

  mqKeys.forEach((mq) => {
    mqLists[mq] = window.matchMedia(mediaQueries[mq]);
    theme.mediaMatches[mq] = mqLists[mq].matches;
    try {
      mqLists[mq].addEventListener('change', handleMqChange);
    } catch {
      mqLists[mq].addListener(handleMqChange);
    }
  });
})();

/* Safe fallback for trapFocus / removeTrapFocus if not provided by theme. */
if (typeof window.trapFocus !== 'function') {
  window.trapFocus = function (el) {
    if (!el) return;
    const focusable = el.querySelectorAll(
      'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first) first.focus({ preventScroll: true });

    function keyHandler(e) {
      if (e.key !== 'Tab' || !focusable.length) return;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === el) {
          e.preventDefault();
          last && last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first && first.focus();
      }
    }

    if (window.__trapFocusEl && window.__trapFocusEl.__trapFocusHandler) {
      window.__trapFocusEl.removeEventListener('keydown', window.__trapFocusEl.__trapFocusHandler);
      delete window.__trapFocusEl.__trapFocusHandler;
    }
    el.__trapFocusHandler = keyHandler;
    el.addEventListener('keydown', keyHandler);
    window.__trapFocusEl = el;
  };
}

if (typeof window.removeTrapFocus !== 'function') {
  window.removeTrapFocus = function () {
    const el = window.__trapFocusEl;
    if (el && el.__trapFocusHandler) {
      el.removeEventListener('keydown', el.__trapFocusHandler);
      delete el.__trapFocusHandler;
    }
    window.__trapFocusEl = null;
  };
}

/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.activeHotspot = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleClickOutside = this.handleClickOutside.bind(this);
      this.handleKeyup = this.handleKeyup.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
      this.syncStageHeight = this.syncStageHeight.bind(this);
    }

    connectedCallback() {
      // Make sure this element is a positioning context even if utility CSS is missing
      const cs = getComputedStyle(this);
      if (cs.position === 'static') this.style.position = 'relative';
      this.style.display = this.style.display || 'block';

      // Ensure each hotspot is actually absolute (don’t rely on a utility class)
      this.querySelectorAll('.hotspot').forEach((hs) => {
        const hsPos = getComputedStyle(hs).position;
        if (hsPos === 'static') hs.style.position = 'absolute';
      });

      // Listen for click delegation on the component
      this.addEventListener('click', this.handleClick);

      // Sync our own height to the rendered image height so % top/left work in Safari/Firefox
      this.imgEl = this.querySelector('img');
      if (this.imgEl) {
        if (this.imgEl.complete) this.syncStageHeight();
        this.imgEl.addEventListener('load', this.syncStageHeight);
        // ResizeObserver catches srcset changes & responsive resizes
        try {
          this.ro = new ResizeObserver(this.syncStageHeight);
          this.ro.observe(this.imgEl);
        } catch {
          window.addEventListener('resize', this.syncStageHeight);
        }
      }

      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('keyup', this.handleKeyup);

      // Auto-open first hotspot ONLY on desktop (md and up)
      requestAnimationFrame(() => {
        if (!theme.mediaMatches.md) return;
        const hotspotToOpen = this.querySelector('.hotspot[data-open-on-load]');
        if (hotspotToOpen) this.activateHotspot(hotspotToOpen);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('keyup', this.handleKeyup);
      if (this.imgEl) this.imgEl.removeEventListener('load', this.syncStageHeight);
      if (this.ro) this.ro.disconnect();
      else window.removeEventListener('resize', this.syncStageHeight);
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
    }

    // Keep container height equal to image height (gives a definite height)
    syncStageHeight() {
      if (!this.imgEl) return;
      const h = this.imgEl.getBoundingClientRect().height;
      if (h > 0) this.style.height = `${h}px`;
    }

    /**
     * Handles 'click' events inside the component (event delegation).
     */
    handleClick(evt) {
      if (!evt.target.matches('.hotspot__btn')) return;
      const hotspot = evt.target.closest('.hotspot');
      if (!hotspot) return;

      if (hotspot === this.activeHotspot) {
        this.closeActiveHotspot(evt);
      } else {
        if (this.activeHotspot) this.closeActiveHotspot(evt);
        this.activateHotspot(hotspot);
      }
    }

    handleClickOutside(evt) {
      if (this.contains(evt.target)) return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    handleKeyup(evt) {
      if (evt.key !== 'Escape') return;
      if (this.activeHotspot) this.closeActiveHotspot(evt);
    }

    handleTransitionEnd(evt) {
      if (this.activeHotspot || evt.propertyName !== 'opacity') return;
      this.classList.add('overflow-hidden');
      if (this.activeCard) {
        this.activeCard.style = null;
        this.activeCard = null;
      }
      this.removeEventListener('transitionend', this.handleTransitionEnd);
    }

    activateHotspot(hotspot) {
      this.activeCard = hotspot.querySelector('.hotspot__card');
      this.checkCardPosition();
      this.classList.remove('overflow-hidden');
      this.setHotspotActiveState(hotspot, true);
      trapFocus(hotspot);

      this.resizeHandler = this.resizeHandler || this.closeActiveHotspot.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    closeActiveHotspot(evt) {
      if (!evt || evt.type !== 'on:debounced-resize') {
        this.addEventListener('transitionend', this.handleTransitionEnd);
      }

      if (this.activeHotspot) {
        this.setHotspotActiveState(this.activeHotspot, false);
        removeTrapFocus();

        if (evt && evt.type === 'on:debounced-resize') {
          this.classList.add('overflow-hidden');
          if (this.activeCard) {
            this.activeCard.style = null;
            this.activeCard = null;
          }
        }
      }
    }

    checkCardPosition() {
      if (!this.activeCard) return;
      const cardRect = this.activeCard.getBoundingClientRect();
      const imageRect = this.getBoundingClientRect();

      if (cardRect.bottom > document.documentElement.clientHeight) {
        this.activeCard.style.top = 'auto';
        this.activeCard.style.bottom = 'calc(100% + 12px)';
      }
      if (theme.mediaMatches.md && cardRect.right > window.innerWidth - 20) {
        this.activeCard.style.left = `calc(50% - ${cardRect.right - window.innerWidth + 45}px)`;
      } else if (theme.mediaMatches.md && cardRect.left < 20) {
        this.activeCard.style.left = `calc(50% - ${cardRect.left - 35}px)`;
      } else if (!theme.mediaMatches.md && cardRect.right > imageRect.right) {
        this.activeCard.style.left = `calc(50% - ${cardRect.right - imageRect.width + 35}px)`;
      } else if (!theme.mediaMatches.md && cardRect.left < imageRect.left) {
        this.activeCard.style.left = `calc(50% - ${cardRect.left - 35}px)`;
      }
    }

    setHotspotActiveState(hotspot, active) {
      hotspot.classList.toggle('is-active', active);
      hotspot.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', active);
      hotspot.querySelector('.hotspot__card')?.setAttribute('aria-hidden', !active);
      this.activeHotspot = active ? hotspot : null;
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}
